import { useScrollReveal } from '../../../hooks/useScrollReveal';
import { useModal } from '../../../contexts/ModalContext';

const localServices = [
  {
    number: '01',
    title: 'Custom Home Design',
    desc: 'Full architectural design for new residential construction in Westchester — tailored to local zoning, site conditions, and architectural context of each specific community.',
  },
  {
    number: '02',
    title: 'Renovations & Additions',
    desc: 'Thoughtful expansion and renovation of existing homes throughout Westchester County — respecting neighborhood character while maximizing spatial quality and property value.',
  },
  {
    number: '03',
    title: 'Residential Site Planning',
    desc: 'Site-specific design that responds to topography, orientation, and municipal requirements unique to Westchester communities and the greater New York metro area.',
  },
  {
    number: '04',
    title: 'Permit-Ready Documentation',
    desc: 'Comprehensive architectural drawings prepared for submission to local building departments in NYC, Westchester, White Plains, Scarsdale, and surrounding municipalities.',
  },
];

export default function LocationServices() {
  const headerRef = useScrollReveal();
  const gridRef = useScrollReveal();
  const mapRef = useScrollReveal();

  return (
    <section className="bg-soft-white">
      {/* Header + Services grid */}
      <div className="max-w-7xl mx-auto px-8 lg:px-16 pt-36 pb-20">
        <div ref={headerRef} className="reveal-on-scroll mb-16">
          <p className="font-sans text-[10px] tracking-[0.22em] uppercase text-bronze mb-4">Serving Your Area</p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-end">
            <h2 className="font-serif text-5xl lg:text-6xl font-light text-dark-ink leading-[1.0]">
              Architecture Services in Westchester, NY
            </h2>
            <p className="font-sans text-sm text-warm-gray leading-[1.85]">
              Providing residential architecture in Westchester, White Plains, Scarsdale, Bronxville, Larchmont, and surrounding areas — with deep local expertise in zoning, building codes, and design character of each community.
            </p>
          </div>
        </div>

        <div ref={gridRef} className="reveal-on-scroll grid grid-cols-1 sm:grid-cols-2 gap-0 border-l border-t border-stone">
          {localServices.map((item, i) => (
            <LocalServiceCard key={i} service={item} delay={i + 1} />
          ))}
        </div>
      </div>

      {/* Map section — full bleed */}
      <div ref={mapRef} className="reveal-scale">
        <div className="relative h-[500px] lg:h-[600px] overflow-hidden">
          <iframe
            title="Talo Architect Location - Scarsdale NY Architecture"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d96718.23!2d-73.9200!3d40.9200!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c293a75e2a43d5%3A0xe4ee2f87ba9b5aed!2sScarsdale%2C%20NY%2010583!5e0!3m2!1sen!2sus!4v1710000000000!5m2!1sen!2sus"
            className="w-full h-full"
            style={{ border: 0, filter: 'grayscale(25%) contrast(1.1) sepia(8%)' }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
          {/* Overlay info card */}
          <div className="absolute bottom-8 left-8 bg-dark-ink/95 backdrop-blur-sm px-8 py-6 max-w-xs">
            <p className="font-sans text-[10px] tracking-[0.18em] uppercase text-bronze mb-3">Studio Location</p>
            <p className="font-serif text-lg font-light text-white mb-1">Talo Architect, P.C.</p>
            <p className="font-sans text-sm text-white/50 mb-4">Scarsdale, NY 10583</p>
            <div className="flex flex-col gap-2">
              <a href="tel:+19146452940" className="font-sans text-sm text-white/60 hover:text-white transition-colors cursor-pointer">
                (914) 645-2940
              </a>
              <a href="mailto:talo.tapan@gmail.com" className="font-sans text-sm text-white/60 hover:text-white transition-colors cursor-pointer">
                talo.tapan@gmail.com
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function LocalServiceCard({
  service,
  delay,
}: {
  service: (typeof localServices)[0];
  delay: number;
}) {
  const ref = useScrollReveal();
  const { openModal } = useModal();

  return (
    <div
      ref={ref}
      className={`reveal-on-scroll reveal-delay-${Math.min(delay, 6)} p-10 border-r border-b border-stone group hover:bg-parchment transition-colors duration-300`}
    >
      <div className="flex items-start justify-between mb-6">
        <span className="font-serif text-4xl font-light text-stone group-hover:text-bronze/30 transition-colors duration-500">
          {service.number}
        </span>
      </div>
      <h3 className="font-serif text-xl font-light text-dark-ink mb-3 group-hover:text-wood transition-colors duration-300">
        {service.title}
      </h3>
      <p className="font-sans text-sm text-warm-gray leading-relaxed mb-6">{service.desc}</p>
      <button
        onClick={openModal}
        className="font-sans text-[10px] tracking-[0.18em] uppercase text-dark-ink hover:text-wood transition-colors cursor-pointer flex items-center gap-3 whitespace-nowrap group/btn"
      >
        <span>Learn More</span>
        <div className="w-4 h-px bg-dark-ink group-hover/btn:w-8 group-hover/btn:bg-wood transition-all duration-300" />
      </button>
    </div>
  );
}
